'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import LandingCertificatePreview from '@/app/public-landing-page/components/LandingCertificatePreview';
import CommunityExplorerFeed from '@/app/public-landing-page/components/CommunityExplorerFeed';
import {
  ErtzyxIconBadge,
  FamilyArchiveIcon,
  GeospatialIcon,
  JourneyMapIcon,
  LivingLedgerIcon,
  ProofCardsIcon,
  PulseIcon,
  SevenPillarsIcon,
  TimeCapsuleIcon,
  VerificationIcon,
} from '@/lib/ertzyxIcons';
import { RAINBOW_GRADIENT, rainbowFrameStyle } from './ledger-rainbow';
import { useTranslation } from '@/i18n/useTranslation';

const featureCards = [
  {
    id: 'preserve',
    titleKey: 'landing_ledger_exp.col1_title',
    bodyKey: 'landing_ledger_exp.col1_desc',
    icon: LivingLedgerIcon,
    accent: '#0d9488',
  },
  {
    id: 'prove',
    titleKey: 'landing_ledger_exp.col2_title',
    bodyKey: 'landing_ledger_exp.col2_desc',
    icon: VerificationIcon,
    accent: '#0891b2',
  },
  {
    id: 'explore',
    titleKey: 'landing_ledger_exp.col3_title',
    bodyKey: 'landing_ledger_exp.col3_desc',
    icon: GeospatialIcon,
    accent: '#7c3aed',
  },
];

const pillars = [
  { labelKey: 'landing_ledger_exp.pillar_history', icon: TimeCapsuleIcon, bg: '#FEF3C7', color: '#B45309' },
  { labelKey: 'landing_ledger_exp.pillar_achievement', icon: ProofCardsIcon, bg: '#D1FAE5', color: '#047857' },
  { labelKey: 'landing_ledger_exp.pillar_performance', icon: PulseIcon, bg: '#DBEAFE', color: '#1D4ED8' },
  { labelKey: 'landing_ledger_exp.pillar_hero', icon: VerificationIcon, bg: '#FEE2E2', color: '#B91C1C' },
  { labelKey: 'landing_ledger_exp.pillar_grit', icon: JourneyMapIcon, bg: '#EDE9FE', color: '#6D28D9' },
  { labelKey: 'landing_ledger_exp.pillar_favorites', icon: FamilyArchiveIcon, bg: '#FCE7F3', color: '#BE185D' },
  { labelKey: 'landing_ledger_exp.pillar_news', icon: GeospatialIcon, bg: '#CFFAFE', color: '#0E7490' },
];

function RainbowCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-xl p-[2px] ${className}`} style={rainbowFrameStyle()}>
      <div className="h-full rounded-[10px] bg-white">{children}</div>
    </div>
  );
}

export default function LedgerExperienceSection() {
  const { t } = useTranslation();
  const [activeFeature, setActiveFeature] = useState<string>('preserve');
  const [activePillar, setActivePillar] = useState<string | null>(null);

  return (
    <section
      id="what-ertzyx"
      className="relative overflow-hidden bg-[#9ADFDB] px-3 py-10 sm:px-5 lg:px-8 xl:px-10"
    >
      <div className="pointer-events-none absolute -left-24 top-20 h-64 w-64 rounded-full bg-white/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-16 bottom-10 h-48 w-48 rounded-full bg-teal-300/30 blur-3xl" />

      <div className="absolute inset-x-0 top-0 h-1 bg-white/60" />
      <div className="relative mx-auto max-w-[1480px] overflow-hidden rounded-t-3xl bg-white shadow-2xl ring-1 ring-black/5">
        <div className="px-5 py-10 text-center sm:px-8 lg:py-12">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
            {t('landing_ledger_exp.eyebrow' as any)}
          </p>
          <h2 className="text-3xl font-black leading-tight tracking-tight text-slate-950 sm:text-4xl">
            {t('landing_ledger_exp.title' as any)}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-base font-medium text-slate-600 sm:text-lg">
            {t('landing_ledger_exp.subtitle' as any)}
          </p>
          <Link href="/register" className="mt-7 inline-flex">
            <button
              type="button"
              className="rounded-xl px-8 py-3.5 text-lg font-black text-slate-950 shadow-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-xl active:scale-95"
              style={{ background: RAINBOW_GRADIENT }}
            >
              {t('landing_ledger_exp.cta' as any)}
            </button>
          </Link>
        </div>
      </div>

      <div className="relative mx-auto grid max-w-[1480px] gap-8 rounded-b-3xl bg-[#E8FAF8] p-4 shadow-2xl ring-1 ring-black/5 sm:p-6 lg:p-8 2xl:grid-cols-[minmax(0,1fr)_minmax(400px,0.88fr)] 2xl:p-10">
        <div className="min-w-0 space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            {featureCards.map((card) => {
              const Icon = card.icon;
              const active = activeFeature === card.id;
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => setActiveFeature(card.id)}
                  className={`rounded-xl border p-5 text-left shadow-sm transition-all duration-200 ${
                    active
                      ? 'border-teal-300 bg-white shadow-md ring-2 ring-teal-400/30'
                      : 'border-teal-200/80 bg-white/90 hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-md'
                  }`}
                >
                  <ErtzyxIconBadge active={active} className="mb-4">
                    <Icon size={22} strokeWidth={1.75} />
                  </ErtzyxIconBadge>
                  <h3 className="text-lg font-black text-slate-950">{t(card.titleKey as any)}</h3>
                  <p className="mt-1.5 text-sm leading-snug text-slate-600">{t(card.bodyKey as any)}</p>
                </button>
              );
            })}
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(200px,0.72fr)_minmax(280px,1fr)]">
            <RainbowCard>
              <div className="p-5 sm:p-6">
                <div className="flex items-center gap-3">
                  <ErtzyxIconBadge size={40} active>
                    <SevenPillarsIcon size={20} />
                  </ErtzyxIconBadge>
                  <h3 className="text-xl font-black text-slate-950">{t('landing_ledger_exp.col4_title' as any)}</h3>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  {t('landing_ledger_exp.col4_desc' as any)}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {pillars.map((pillar) => {
                    const Icon = pillar.icon;
                    const label = t(pillar.labelKey as any);
                    const active = activePillar === pillar.labelKey;
                    return (
                      <button
                        key={pillar.labelKey}
                        type="button"
                        onMouseEnter={() => setActivePillar(pillar.labelKey)}
                        onMouseLeave={() => setActivePillar(null)}
                        onFocus={() => setActivePillar(pillar.labelKey)}
                        onBlur={() => setActivePillar(null)}
                        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-sm font-bold transition-transform duration-150 hover:scale-105"
                        style={{
                          background: pillar.bg,
                          color: pillar.color,
                          boxShadow: active ? `0 0 0 2px ${pillar.color}44` : undefined,
                        }}
                      >
                        <Icon size={14} />
                        {label}
                      </button>
                    );
                  })}
                </div>
                {activePillar && (
                  <p className="mt-3 text-xs font-medium text-slate-600">
                    Organize and discover records under{' '}
                    <span className="font-bold">{t(activePillar as any)}</span>.
                  </p>
                )}
              </div>
            </RainbowCard>

            <RainbowCard className="transition-shadow duration-300 hover:shadow-lg">
              <div className="p-5 sm:p-6">
                <LandingCertificatePreview />
              </div>
            </RainbowCard>
          </div>
        </div>

        <CommunityExplorerFeed />
      </div>
    </section>
  );
}
